import type { ComponentSchema } from '../../../../../schema-types'
import { SECTION_STYLE_FIELDS, ICON_OPTIONS } from './_fields'

/**
 * Torongo hero: a slider (headline slide, then product or custom slides) beside two side banners (picked products or
 * the biggest deals), with the trust strip under both. Slide keys match the shared hero (`title`, `subtitle`, `image`,
 * `ctaText`, `ctaLink`, `badge`), so slides from other themes keep showing.
 */
export const schema: ComponentSchema = {
  type: 'hero',
  label: 'Hero',
  description: 'Slider with side banners and a trust strip (delivery, warranty, cash on delivery, hotline).',
  supportedPages: ['home', 'shop', 'category', 'categories', 'cart', 'checkout', 'order-success'],
  variants: [{ id: 'v1', label: 'Slider with two side banners' }],
  commonFields: [
    { key: 'eyebrow', label: 'Eyebrow', type: 'localized-string', default: 'New this week' },
    { key: 'headline', label: 'Headline', type: 'localized-string', default: 'Big-screen cinema at home' },
    { key: 'subtext', label: 'Text', type: 'localized-text', default: 'Projectors, TV boxes and sound for every room, with official warranty and cash on delivery.' },
    { key: 'image', label: 'Picture', type: 'image', help: 'Shown beside the text on large screens. Empty = the first product card’s photo.' },
    { key: 'imageAlt', label: 'Picture description', type: 'string', help: 'For screen readers. Leave empty if the picture is decorative.' },
    { key: 'primaryCta.label', label: 'Main button text', type: 'localized-string', default: 'Shop now' },
    { key: 'primaryCta.url', label: 'Main button link', type: 'string', default: '/shop' },
    { key: 'secondaryCta.label', label: 'Second button text', type: 'localized-string', default: 'Today’s offers' },
    { key: 'secondaryCta.url', label: 'Second button link', type: 'string', default: '/shop?order_by=discount_desc' },
    { key: 'showTrustChips', label: 'Show the trust strip', type: 'boolean', default: true },
    {
      key: 'trustChips',
      label: 'Trust strip',
      help: 'Up to four.',
      type: 'array',
      dependency: { field: 'showTrustChips', value: true },
      itemSchema: [
        { key: 'icon', label: 'Icon', type: 'icon', default: 'truck', options: ICON_OPTIONS },
        { key: 'text', label: 'Text', type: 'localized-string' },
      ],
      default: [
        { icon: 'truck', text: 'Delivery all over Bangladesh' },
        { icon: 'shield', text: 'Official warranty' },
        { icon: 'cash', text: 'Cash on delivery' },
        { icon: 'headset', text: 'Hotline support' },
      ],
    },
    { key: 'showCards', label: 'Show the side banners', type: 'boolean', default: true },
    {
      key: 'cards',
      label: 'Side banners',
      type: 'array',
      help: 'Up to two, beside the slider on desktop. Empty = the two biggest current deals.',
      dependency: { field: 'showCards', value: true },
      itemSchema: [
        { key: 'productId', label: 'Product', type: 'product-select' },
        { key: 'label', label: 'Label', type: 'localized-string', placeholder: 'Hot deal' },
      ],
      default: [],
    },
    {
      key: 'slides',
      label: 'More slides',
      type: 'array',
      help: 'Shown after the headline slide. Pick a product, or write your own text and add a picture.',
      itemSchema: [
        { key: 'productId', label: 'Product', type: 'product-select' },
        { key: 'badge', label: 'Badge', type: 'localized-string', placeholder: 'New' },
        { key: 'title', label: 'Headline', type: 'localized-string', help: 'Empty = the product name.' },
        { key: 'subtitle', label: 'Text', type: 'localized-text' },
        { key: 'image', label: 'Picture', type: 'image', help: 'Empty = the product photo.' },
        { key: 'ctaText', label: 'Button text', type: 'localized-string', placeholder: 'View product' },
        { key: 'ctaLink', label: 'Button link', type: 'string', help: 'Empty = the product page.' },
      ],
      default: [],
    },
    { key: 'autoplay', label: 'Play slides automatically', type: 'boolean', default: true },
    { key: 'autoplaySeconds', label: 'Seconds per slide', type: 'number', default: 6, help: '0 or empty = 6 seconds.', dependency: { field: 'autoplay', value: true } },
    ...SECTION_STYLE_FIELDS,
  ],
}
